import type { AnyRealtimeRun } from "@trigger.dev/core/v3"
import type { ComputedRef, Ref } from "vue"

export type MaybeRefOrGetter<T> = T | Ref<T> | (() => T)

export const TERMINAL_RUN_STATUSES = new Set([
	"COMPLETED",
	"FAILED",
	"CANCELED",
	"CRASHED",
	"TIMED_OUT",
	"EXPIRED",
	"SYSTEM_FAILURE",
])

/**
 * Statuses where the run is actively moving toward completion. Deliberately an
 * allow-list: the vendor keeps adding statuses, and the non-terminal ones it
 * has added so far (`PENDING_VERSION`, `DELAYED`, `WAITING`) mean "parked", not
 * "progressing". Callers that block UI on a run must fail open for anything
 * they do not recognise.
 */
export const PROGRESSING_RUN_STATUSES = new Set([
	"QUEUED",
	"DEQUEUED",
	"EXECUTING",
])

/**
 * Non-terminal statuses the platform resumes on its own: a `WAITING` run
 * continues after its wait completes, a `DELAYED` run enters the queue at its
 * scheduled time, a `PENDING_VERSION` run executes once its version deploys,
 * and a retrying run re-enters execution. They are not progressing, but they
 * are not finished either, so a caller must keep tracking them instead of
 * presenting them as over. `WAITING_FOR_DEPLOY` and `REATTEMPTING` are the
 * legacy names the API used for the last two conditions.
 */
export const RESUMABLE_RUN_STATUSES = new Set([
	"PENDING_VERSION",
	"WAITING_FOR_DEPLOY",
	"WAITING",
	"DELAYED",
	"REATTEMPTING",
])

export const FAILED_RUN_STATUSES = new Set([
	"FAILED",
	"CRASHED",
	"TIMED_OUT",
	"EXPIRED",
	"SYSTEM_FAILURE",
])

export function isTerminalRunStatus(status: string | undefined): boolean {
	return status !== undefined && TERMINAL_RUN_STATUSES.has(status)
}

export interface RealtimeHookBaseOptions {
	accessToken: MaybeRefOrGetter<string | undefined>
	baseURL?: MaybeRefOrGetter<string | undefined>
	enabled?: MaybeRefOrGetter<boolean>
	onComplete?: (run: AnyRealtimeRun | undefined, error: Error | null) => void
}

export interface RealtimeRunHookOptions extends RealtimeHookBaseOptions {}

export interface RealtimeRunsWithTagHookOptions
	extends RealtimeHookBaseOptions {}

export interface RealtimeRunWithStreamsHookOptions<
	TStreams extends Record<string, unknown> = Record<string, unknown>,
> extends RealtimeHookBaseOptions {
	streamKeys?: ReadonlyArray<keyof TStreams & string>
}

export interface RealtimeRunHookResult {
	run: Ref<AnyRealtimeRun | undefined>
	error: Ref<Error | null>
	isLoading: ComputedRef<boolean>
}

export interface RealtimeRunsWithTagHookResult {
	runs: ComputedRef<AnyRealtimeRun[]>
	error: Ref<Error | null>
	isLoading: ComputedRef<boolean>
}

export interface RealtimeStreamPart<
	TStreams extends Record<string, unknown> = Record<string, unknown>,
> {
	key: keyof TStreams & string
	chunk: TStreams[keyof TStreams]
}

export interface RealtimeRunWithStreamsHookResult<
	TStreams extends Record<string, unknown> = Record<string, unknown>,
> {
	run: Ref<AnyRealtimeRun | undefined>
	parts: ComputedRef<ReadonlyArray<RealtimeStreamPart<TStreams>>>
	error: Ref<Error | null>
	isLoading: ComputedRef<boolean>
}

export type RunListener = (run: AnyRealtimeRun) => void

export type RunWithStreamsListener<
	TStreams extends Record<string, unknown> = Record<string, unknown>,
> = (
	event:
		| { type: "run"; run: AnyRealtimeRun }
		| { type: "part"; part: RealtimeStreamPart<TStreams> },
) => void

export interface RunSubscriptionOptions {
	runId: string
	accessToken: string
	baseURL?: string
	onError: (error: Error) => void
}

export interface TagSubscriptionOptions {
	tag: string
	accessToken: string
	baseURL?: string
	onError: (error: Error) => void
}

export interface RunWithStreamsSubscriptionOptions {
	runId: string
	accessToken: string
	baseURL?: string
	onError: (error: Error) => void
}

// Mirrors `RunSubscription.withStreams()` from `@trigger.dev/core/v3` —
// keep the discriminator shape aligned with the upstream SDK.
export type RunWithStreamsEvent<
	TStreams extends Record<string, unknown> = Record<string, unknown>,
> =
	| { type: "run"; run: AnyRealtimeRun }
	| {
			type: keyof TStreams & string
			chunk: TStreams[keyof TStreams]
			run: AnyRealtimeRun
	  }

export interface RealtimeSubscriptionClient {
	subscribeToRun(
		runId: string,
		options: RealtimeClientOptions,
	): AsyncIterable<AnyRealtimeRun>
	subscribeToRunsWithTag(
		tag: string,
		options: RealtimeClientOptions,
	): AsyncIterable<AnyRealtimeRun>
	subscribeToRunWithStreams(
		runId: string,
		options: RealtimeClientOptions,
	): AsyncIterable<RunWithStreamsEvent>
}

export interface RealtimeClientOptions {
	accessToken: string
	baseURL?: string
	signal: AbortSignal
}
